import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../layout/header";
import {
  fetchAllTecgymAdminAppActivities,
  fetchTecgymAdminUsers,
  filterAppActivitiesByUser,
  getAdminUserDisplayNames,
  getAppActivityValue,
  getUserActivitySuccessMessage,
  getVisibleAdminUsers,
  setAppActivityUserNames,
  slugToUserName,
  userNameToSlug
} from "../features/appActivity/appActivityData";
import { canViewAppActivity, getAdminUser, isLoggedInAdmin } from "../libraries/auth";
import { showToast } from "../libraries/clipboardUtils";
import {
  canViewAppActivityUserDetails,
  canViewAppActivityUsers,
  getDefaultAccessibleRoute
} from "../libraries/privileges";

const SELECT_USER_PLACEHOLDER = "";
const ALL_ACTIVITY_TYPE = "All";

const ACTIVITY_TABLE_COLUMNS = [
  { key: "activityType", label: "Activity Type" },
  { key: "activityBy", label: "Activity By" },
  { key: "activityDate", label: "Activity Date" },
  { key: "initiatedFrom", label: "Initiated From" },
  { key: "previousValue", label: "Previous Value" },
  { key: "updatedValue", label: "Updated Value" },
  { key: "createdDate", label: "Created Date" }
];

const formatActivityDate = (value) => {
  if (!value) return "N/A";

  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return String(value);
  }
};

const formatActivityCellValue = (activity, field) => {
  const value = getAppActivityValue(activity, field);

  if (value === "" || value === null || value === undefined) {
    return "N/A";
  }

  if (field === "activityDate" || field === "createdDate") {
    return formatActivityDate(value);
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "N/A";
    }
  }

  return String(value);
};

function AppActivityPage() {
  const navigate = useNavigate();
  const { userSlug } = useParams();
  const canAccessAppActivity = canViewAppActivity();
  const canListActivityUsers = canViewAppActivityUsers();
  const canViewUserActivity = canViewAppActivityUserDetails();
  const canLoadSelectedUserActivity = canViewUserActivity || canListActivityUsers;
  const isAdminUser = isLoggedInAdmin();
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [activityUsers, setActivityUsers] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState("");
  const [activitiesLoadMessage, setActivitiesLoadMessage] = useState("");
  const [allActivities, setAllActivities] = useState([]);
  const [selectedActivityType, setSelectedActivityType] = useState(ALL_ACTIVITY_TYPE);

  const selectedUser = useMemo(() => {
    if (activityUsers.length === 0 || !userSlug) {
      return SELECT_USER_PLACEHOLDER;
    }

    const slugUser = slugToUserName(userSlug);
    return slugUser && activityUsers.includes(slugUser) ? slugUser : SELECT_USER_PLACEHOLDER;
  }, [activityUsers, userSlug]);

  useEffect(() => {
    if (!canAccessAppActivity) {
      navigate(getDefaultAccessibleRoute(), { replace: true });
    }
  }, [canAccessAppActivity, navigate]);

  useEffect(() => {
    if (!canAccessAppActivity || !canListActivityUsers) {
      return;
    }

    let isCancelled = false;

    setUsersLoading(true);
    setUsersError("");

    fetchTecgymAdminUsers()
      .then((users) => {
        if (isCancelled) {
          return;
        }

        const visibleUsers = getVisibleAdminUsers(users, getAdminUser());
        const displayNames = getAdminUserDisplayNames(visibleUsers);
        setAppActivityUserNames(displayNames);
        setActivityUsers(displayNames);
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }

        setUsersError(error?.message || "Failed to load users.");
      })
      .finally(() => {
        if (!isCancelled) {
          setUsersLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [canAccessAppActivity, canListActivityUsers]);

  useEffect(() => {
    if (!canAccessAppActivity || !canLoadSelectedUserActivity || !selectedUser || !isAdminUser) {
      setAllActivities([]);
      setActivitiesError("");
      setActivitiesLoadMessage("");
      setActivitiesLoading(false);
      return;
    }

    let isCancelled = false;

    setActivitiesLoading(true);
    setActivitiesError("");

    fetchAllTecgymAdminAppActivities()
      .then(({ activities }) => {
        if (isCancelled) {
          return;
        }

        const userActivities = filterAppActivitiesByUser(activities, selectedUser);
        const message = getUserActivitySuccessMessage(userActivities, selectedUser);

        setAllActivities(activities);
        setActivitiesLoadMessage(message);
        showToast(message, "success");
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }

        const errorMessage = error?.message || "Failed to load app activities.";
        setActivitiesError(errorMessage);
        setActivitiesLoadMessage("");
        setAllActivities([]);
        showToast(errorMessage, "error");
      })
      .finally(() => {
        if (!isCancelled) {
          setActivitiesLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [canAccessAppActivity, canLoadSelectedUserActivity, isAdminUser, selectedUser]);

  useEffect(() => {
    if (userSlug && activityUsers.length > 0 && !selectedUser) {
      navigate("/app-activity", { replace: true });
    }
  }, [activityUsers.length, navigate, selectedUser, userSlug]);

  useEffect(() => {
    setSelectedActivityType(ALL_ACTIVITY_TYPE);
  }, [selectedUser]);

  const userActivities = useMemo(() => {
    const activities = filterAppActivitiesByUser(allActivities, selectedUser);

    return [...activities].sort((first, second) => {
      const firstDate = new Date(
        getAppActivityValue(first, "activityDate") ||
        getAppActivityValue(first, "createdDate")
      ).getTime();
      const secondDate = new Date(
        getAppActivityValue(second, "activityDate") ||
        getAppActivityValue(second, "createdDate")
      ).getTime();

      return secondDate - firstDate;
    });
  }, [allActivities, selectedUser]);

  const activityTypeOptions = useMemo(() => {
    const types = new Set();

    userActivities.forEach((activity) => {
      const type = String(getAppActivityValue(activity, "activityType")).trim();
      if (type) {
        types.add(type);
      }
    });

    return [
      ALL_ACTIVITY_TYPE,
      ...Array.from(types).sort((first, second) => first.localeCompare(second))
    ];
  }, [userActivities]);

  const filteredActivities = useMemo(() => {
    if (selectedActivityType === ALL_ACTIVITY_TYPE) {
      return userActivities;
    }

    return userActivities.filter(
      (activity) => getAppActivityValue(activity, "activityType") === selectedActivityType
    );
  }, [userActivities, selectedActivityType]);

  if (!canAccessAppActivity) {
    return null;
  }

  const handleUserChange = (event) => {
    const userName = event.target.value;

    if (!userName) {
      navigate("/app-activity");
      return;
    }

    navigate(`/app-activity/${userNameToSlug(userName)}`);
  };

  const hasActiveFilters =
    Boolean(selectedUser) || selectedActivityType !== ALL_ACTIVITY_TYPE;

  const handleReset = () => {
    setSelectedActivityType(ALL_ACTIVITY_TYPE);
    navigate("/app-activity");
  };

  const handleActivityTypeChange = (event) => {
    setSelectedActivityType(event.target.value);
  };

  return (
    <div className="main">
      <Header />
      <div
        style={{
          padding: "clamp(16px, 3vw, 20px) clamp(12px, 3vw, 20px)",
          maxWidth: "100%",
          boxSizing: "border-box",
          minHeight: "calc(100vh - 80px)",
          background: "linear-gradient(135deg, #1e2127 0%, #2a2d35 100%)"
        }}
      >
        <h1
          style={{
            margin: "0 0 8px 0",
            color: "#fff",
            fontSize: "clamp(22px, 5vw, 28px)",
            fontWeight: 700
          }}
        >
          Employee Activity
        </h1>
        <p style={{ margin: "0 0 28px 0", color: "#9ca3af", fontSize: "15px" }}>
          Select a user to view their activity in the admin app
        </p>

        {canListActivityUsers ? (
          <>
            {usersError && (
              <p style={{ margin: "0 0 16px 0", color: "#f87171", fontSize: "15px" }}>
                {usersError}
              </p>
            )}

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                alignItems: "flex-end",
                width: "100%",
                boxSizing: "border-box",
                marginBottom: "24px",
                padding: "16px",
                backgroundColor: "#20232b",
                border: "1px solid #3a3f4b",
                borderRadius: "10px",
                maxWidth: "100%"
              }}
            >
              <div style={filterGroupStyle}>
                <label
                  htmlFor="app-activity-user"
                  style={filterLabelStyle}
                >
                  Select User
                </label>

                {usersLoading && activityUsers.length === 0 ? (
                  <p style={{ margin: 0, color: "#9ca3af", fontSize: "14px", padding: "10px 0" }}>
                    Loading users...
                  </p>
                ) : !usersLoading && !usersError && activityUsers.length === 0 ? (
                  <p style={{ margin: 0, color: "#9ca3af", fontSize: "14px", padding: "10px 0" }}>
                    No users found.
                  </p>
                ) : (
                  <select
                    id="app-activity-user"
                    value={selectedUser}
                    onChange={handleUserChange}
                    style={filterSelectStyle(selectedUser ? "#fff" : "#9ca3af")}
                  >
                    <option value={SELECT_USER_PLACEHOLDER}>Select User</option>
                    {activityUsers.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div style={{ ...filterGroupStyle, width: "min(240px, 100%)", maxWidth: "100%", boxSizing: "border-box" }}>
                <label
                  htmlFor="app-activity-type"
                  style={filterLabelStyle}
                >
                  Activity Type
                </label>
                <select
                  id="app-activity-type"
                  value={selectedActivityType}
                  onChange={handleActivityTypeChange}
                  disabled={!selectedUser || activitiesLoading}
                  style={{
                    ...filterSelectStyle("#fff"),
                    opacity: !selectedUser || activitiesLoading ? 0.6 : 1,
                    cursor: !selectedUser || activitiesLoading ? "not-allowed" : "pointer"
                  }}
                >
                  {activityTypeOptions.map((type) => (
                    <option key={type} value={type}>
                      {type === ALL_ACTIVITY_TYPE ? "All Activity" : type}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={handleReset}
                disabled={!hasActiveFilters}
                style={{
                  flexShrink: 0,
                  padding: "10px 20px",
                  backgroundColor: hasActiveFilters ? "#444" : "#333",
                  border: "1px solid #3a3f4b",
                  borderRadius: "8px",
                  color: hasActiveFilters ? "#fff" : "#6b7280",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: hasActiveFilters ? "pointer" : "not-allowed",
                  outline: "none",
                  height: "42px"
                }}
              >
                Reset
              </button>
            </div>

            {isAdminUser && canLoadSelectedUserActivity && selectedUser && (
              <>
                <h2
                  style={{
                    margin: "0 0 8px 0",
                    color: "#fff",
                    fontSize: "22px",
                    fontWeight: 700
                  }}
                >
                  {selectedUser} — Employee Activity
                </h2>
                <p style={{ margin: "0 0 24px 0", color: "#9ca3af", fontSize: "15px" }}>
                  All activity by {selectedUser} in the admin app
                </p>

                {activitiesError && (
                  <p style={{ margin: "0 0 16px 0", color: "#f87171", fontSize: "15px" }}>
                    {activitiesError}
                  </p>
                )}

                <div
                  style={{
                    backgroundColor: "#1a1d23",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                    border: "1px solid #3a3f4b"
                  }}
                >
                  {activitiesLoading ? (
                    <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>
                      Loading activities...
                    </div>
                  ) : filteredActivities.length === 0 ? (
                    <div style={{ padding: "40px", textAlign: "center", color: "#4ade80" }}>
                      {selectedActivityType !== ALL_ACTIVITY_TYPE
                        ? `No records found for "${selectedActivityType}".`
                        : activitiesLoadMessage || "No records found."}
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "14px"
                        }}
                      >
                        <thead>
                          <tr style={{ backgroundColor: "#2a2d35", borderBottom: "2px solid #187718" }}>
                            {ACTIVITY_TABLE_COLUMNS.map((column) => (
                              <th key={column.key} style={tableHeaderStyle}>
                                {column.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredActivities.map((activity, index) => (
                            <tr
                              key={activity._id || activity.id || `${selectedUser}-${index}`}
                              style={{
                                borderBottom: "1px solid #333",
                                backgroundColor: index % 2 === 0 ? "#1a1d23" : "#20232b"
                              }}
                            >
                              {ACTIVITY_TABLE_COLUMNS.map((column) => (
                                <td key={column.key} style={tableCellStyle}>
                                  {column.key === "activityType" ? (
                                    <span style={activityTypeBadgeStyle}>
                                      {formatActivityCellValue(activity, column.key)}
                                    </span>
                                  ) : (
                                    formatActivityCellValue(activity, column.key)
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <p style={{ margin: 0, color: "#9ca3af", fontSize: "15px" }}>
            You do not have permission to view activity users.
          </p>
        )}
      </div>
    </div>
  );
}

const filterGroupStyle = {
  width: "min(200px, 100%)",
  flex: "1 1 180px",
  minWidth: 0,
  maxWidth: "100%",
  boxSizing: "border-box"
};

const filterLabelStyle = {
  display: "block",
  marginBottom: "8px",
  color: "#fff",
  fontWeight: 700,
  fontSize: "14px"
};

const filterSelectStyle = (color = "#fff") => ({
  width: "100%",
  backgroundColor: "#12151d",
  border: "1px solid #3a3f4b",
  borderRadius: "8px",
  color,
  padding: "10px 12px",
  fontSize: "14px",
  outline: "none",
  height: "42px"
});

const tableHeaderStyle = {
  padding: "14px 16px",
  textAlign: "left",
  fontWeight: 600,
  color: "#4ade80",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  fontSize: "12px",
  whiteSpace: "nowrap"
};

const tableCellStyle = {
  padding: "14px 16px",
  color: "#fff",
  verticalAlign: "top",
  maxWidth: "280px",
  wordBreak: "break-word"
};

const activityTypeBadgeStyle = {
  display: "inline-block",
  padding: "4px 10px",
  borderRadius: "4px",
  backgroundColor: "rgba(24, 119, 24, 0.2)",
  color: "#4ade80",
  fontSize: "13px",
  fontWeight: 600
};

export default AppActivityPage;
